import { Flex, TooltipTrigger, Button, TooltipContent, TabBar, Tooltip } from "@repo/ping-design";
import { useState, useCallback, type CSSProperties } from "react";
import {
  MISSING_VALUE,
  type LatLngType,
  type BuildingPanelSection,
  type BuildingPanelField,
  getRiskScalePosition,
} from "@repo/ping-react-js";
import "./TopSection.scss";

const INSET_MAP_ENABLED = true;

export const TopSection = ({ latLng, section }: { latLng: LatLngType; section: BuildingPanelSection }) => {
  const [mapType, setMapType] = useState<string>("satellite");
  const [zoom, setZoom] = useState<number>(18);

  const renderField = useCallback(
    (field: any) => {
      const displayValue = field?.v ?? MISSING_VALUE;

      if (section.type === "MAPS") {
        return (
          <Flex
            key={field.k}
            direction="row"
            gap="3"
            align="center"
            justify="space-between"
            className="TopSection__ValueSection__Data__Maps"
          >
            <span className="TopSection__ValueSection__Data__Maps__Label">{field.k}</span>
            <span className="TopSection__ValueSection__Data__Maps__Value">{displayValue}</span>
          </Flex>
        );
      } else if (section.type === "PLI") {
        const pliField = field as BuildingPanelField;
        const position = getRiskScalePosition(pliField.riskScale, pliField.v);
        const activeIndex = position?.activeIndex ?? null;
        const activeSegment = activeIndex !== null ? position?.segments[activeIndex] : undefined;

        return (
          <div key={pliField.k} className="TopSection__RiskCard">
            <div className="TopSection__RiskCard__Header">
              <span className="TopSection__RiskCard__Label">{pliField.k}</span>
              <span
                className="TopSection__RiskCard__Badge"
                style={
                  activeSegment
                    ? ({
                        "--risk-badge-bg": activeSegment.background,
                        "--risk-badge-text": activeSegment.text,
                      } as CSSProperties)
                    : undefined
                }
              >
                {String(displayValue)}
              </span>
            </div>
            {position && (
              <div className="TopSection__RiskCard__Bar">
                {position.segments.map((segment, index) => (
                  <div
                    key={index}
                    className="TopSection__RiskCard__Segment"
                    style={{ "--risk-segment-color": segment.color } as CSSProperties}
                  >
                    {index === activeIndex && (
                      <svg className="TopSection__RiskCard__Marker" viewBox="15.9 7.9 11.8 10.6" aria-hidden="true">
                        <path d="M22.333 18.0915C22.0779 18.5455 21.4239 18.5447 21.1699 18.0901L16.0862 8.993C15.8148 8.50733 16.2226 7.92251 16.7722 8.00928L21.6465 8.77892C21.7152 8.78977 21.7852 8.7898 21.8539 8.77901L26.7606 8.00854C27.3107 7.92216 27.718 8.50828 27.4452 8.99372L22.333 18.0915Z" />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
    },
    [section.type],
  );

  return (
    <Flex className="TopSection" direction="row" gap="8" justify="space-between">
      <Flex direction="column" className="TopSection__ValueSection" gap="2">
        <div className={section.type === "PLI" ? "TopSection__RiskGrid" : "TopSection__ValueSection__Data"}>
          {section?.values?.map((field: any) => {
            return renderField(field);
          })}
        </div>
      </Flex>
      {section.type !== "PLI" && INSET_MAP_ENABLED && (
        <Flex className="TopSection__MapSection" direction="column" gap="2">
          {isNaN(latLng?.lat) || isNaN(latLng?.lng) ? (
            <div className="TopSection__MapSection__Image">
              <div className="TopSection__MapSection__Image--NotAvailable">
                <span>Map not available</span>
              </div>
            </div>
          ) : (
            <img
              src={
                isNaN(latLng?.lat) || isNaN(latLng?.lng)
                  ? undefined
                  : `http://maps.google.com/maps/api/staticmap?zoom=${zoom}&size=350x350&maptype=${mapType}&markers=${latLng?.lat},${latLng?.lng}&sensor=false&key=${import.meta.env.VITE_APP_PING_GOOGLE_API_KEY}`
              }
              className="TopSection__MapSection__Image"
            />
          )}
          <Flex direction="row" justify="space-between">
            <Flex direction="row" gap="1">
              <Tooltip placement="bottom">
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    iconName="Plus"
                    size="sm"
                    onClick={() => {
                      setZoom((prev) => prev + 1);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>Zoom In</TooltipContent>
              </Tooltip>
              <Tooltip placement="bottom">
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    iconName="Minus"
                    size="sm"
                    onClick={() => {
                      setZoom((prev) => prev - 1);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>Zoom Out</TooltipContent>
              </Tooltip>
            </Flex>
            <TabBar
              variant="sm"
              selected={mapType}
              className="TopSection__MapSection__TabBar"
              options={[
                {
                  id: "satellite",
                  label: "Satellite",
                },
                {
                  id: "roadmap",
                  label: "Map",
                },
              ]}
              onChange={(id) => {
                setMapType(id);
              }}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
